import * as React from 'react';
// Import material ui modules
import {
	FileCopy,
	Payment,
	PictureAsPdf,
	Replay,
	Search,
} from '@mui/icons-material';
// Import hooks.
import {
	useDivWrapper,
	UseDivWrapperResults,
} from '@app/hooks/view';
import {
	useInputButton,
	UseInputButtonResult,
} from '@app/hooks/view/input';
// Import props.
import {
	FormReviewProps,
	FormProps,
	ScrollbarProps,
	StatusProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	ButtonSize,
} from '@app/enumerations';

interface UseFormReviewParameters {
	id: string;
	props: FormReviewProps,
}

interface UseFormReviewResults {
	statusDivWrapper: UseDivWrapperResults;
	proceedWithPaymentButton: UseInputButtonResult;
	underwritingReviewButton: UseInputButtonResult;
	newBusinessApplicationPDFButton: UseInputButtonResult;
	copyNewBusinessApplicationButton: UseInputButtonResult;
	resubmitNewBusinessApplicationButton: UseInputButtonResult;
	digitalSignaturesButton: UseInputButtonResult;
	children: JSX.Element[] | JSX.Element | undefined;
	isUnderwritingReviewButtonVisible: () => boolean;
	isResubmitNewBusinessApplicationButtonVisible: () => boolean;
	isDigitalSignaturesButtonVisible: () => boolean;
	applyFormProps: () => FormProps & ScrollbarProps;
}

const useFormReview = (params: UseFormReviewParameters): UseFormReviewResults => {

	// #region exported methods

	const incrementReadyCount = (): void => {
		setReadyCount((readyCount: number) => {
			return readyCount + 1;
		});
	}

	/**
	 * Checks to see if the underwriting review button is visible. 
	 **/
	const isUnderwritingReviewButtonVisible = (): boolean => {
		return params.props.onViewUnderwritingReview !== undefined;
	}

	/**
	 * Checks to see if the resubmit new business application button is visible. 
	 **/
	const isResubmitNewBusinessApplicationButtonVisible = (): boolean => {
		return params.props.onResubmitNewBusinessApplication !== undefined;
	}

	/**
	 * Checks to see if the digital signatures button is visible.
	 **/
	const isDigitalSignaturesButtonVisible = (): boolean => {
		return params.props.onViewDigitalSignatures !== undefined;
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Pay & Issue Policy',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: false,
			statusProps: applyStatusProps(),
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Returns props for the status. 
	 **/
	const applyStatusProps = (): StatusProps | undefined => {
		let statusProps: StatusProps | undefined = undefined;
		if (showStatusMessage) {
			statusProps = {
				statusMessage: params.props.statusMessage ?? String.empty(),
				statusType: AppThemeColor.ERROR,
			}
		}

		return statusProps;
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generate id for the status div wrapper.
	 **/
	const generateIdForStatusDivWrapper = (): string => {
		return `${generateIdForForm()}-status-divWrapper`;
	}

	/**
	 * Generate id for the proceed with payment button.
	 **/
	const generateIdForProceedWithPaymentButton = (): string => {
		return `${generateIdForForm()}-proceedWithPayment-button`;
	}

	/**
	 * Generate id for the underwriting review button.
	 **/
	const generateIdForUnderwritingReviewButton = (): string => {
		return `${generateIdForForm()}-underwritingReview-button`;
	}

	/**
	 * Generate id for the new business application PDF button. 
	 **/
	const generateIdForNewBusinessApplicationPDFButton = (): string => {
		return `${generateIdForForm()}-newBusinessApplicationPDF-button`;
	}

	/**
	 * Generate id for copy new business application button. 
	 **/
	const generateIdForCopyNewBusinessApplicationButton = (): string => {
		return `${generateIdForForm()}-copyNewBusinessApplication-button`;
	}

	/**
	 * Generate id for resubmit new business application button. 
	 **/
	const generateIdForResubmitNewBusinessApplicationButton = (): string => {
		return `${generateIdForForm()}-resubmitNewBusinessApplication-button`;
	}

	//#endregion id generators

	/**
	 * Checks to see if the proceed with payment button is disabled. 
	 **/
	const isProceedWithPaymentButtonDisabled = (): boolean => {
		return params.props.onProceedWithPayment === undefined;
	}

	/**
	 * Checks to see if the resubmit new business application button is disabled. 
	 **/
	const isResubmitNewBusinessApplicationButtonDisabled = (): boolean => {
		return !isResubmitNewBusinessApplicationButtonVisible();
	}

	/**
	 * Checks to see if the digital signatures button is disabled. 
	 **/
	const isDigitalSignaturesButtonDisabled = (): boolean => {
		return !isDigitalSignaturesButtonVisible();
	}

	/**
	 * Checks to see if the underwriting review button is disabled. 
	 **/
	const isUnderwritingReviewButtonDisabled = (): boolean => {
		return !isUnderwritingReviewButtonVisible();
	}

	/**
	 * Checks to see if the new business application PDF button is disabled. 
	 **/
	const isNewBusinessApplicationPDFButtonDisabled = (): boolean => {
		return params.props.onDownloadPdfDocument === undefined;
	}

	/**
	 * Checks to see if the copy new business application button is disabled. 
	 **/
	const isCopyNewBusinessApplicationButtonDisabled = (): boolean => {
		return params.props.onCopyNewBusinessApplication === undefined;
	}

	// #endregion private methods


	// #region variables

	// Setup the status div wrapper 
	const statusDivWrapper: UseDivWrapperResults = useDivWrapper({
		id: generateIdForStatusDivWrapper(),
	});

	// Setup the proceed with payment button hook.
	const proceedWithPaymentButton: UseInputButtonResult = useInputButton({
		id: generateIdForProceedWithPaymentButton(),
		label: 'Proceed With Payment',
		onClick: params.props.onProceedWithPayment,
		size: ButtonSize.SMALL,
		buttonType: AppThemeColor.SECONDARY,
		endAdornmentIcon: Payment,
		disabled: isProceedWithPaymentButtonDisabled(),
	});

	// Setup the underwriting review button hook.
	const underwritingReviewButton: UseInputButtonResult = useInputButton({
		id: generateIdForUnderwritingReviewButton(),
		label: 'Underwriting Review',
		onClick: params.props.onViewUnderwritingReview,
		size: ButtonSize.SMALL,
		buttonType: AppThemeColor.SECONDARY,
		endAdornmentIcon: Search,
		disabled: isUnderwritingReviewButtonDisabled(),
	});

	// Setup the new business application PDF button hook.
	const newBusinessApplicationPDFButton: UseInputButtonResult = useInputButton({
		id: generateIdForNewBusinessApplicationPDFButton(),
		label: 'Application PDF',
		onClick: params.props.onDownloadPdfDocument,
		size: ButtonSize.SMALL,
		buttonType: AppThemeColor.SECONDARY,
		endAdornmentIcon: PictureAsPdf,
		disabled: isNewBusinessApplicationPDFButtonDisabled(),
	});

	// Setup the copy new business application button hook.
	const copyNewBusinessApplicationButton: UseInputButtonResult = useInputButton({
		id: generateIdForCopyNewBusinessApplicationButton(),
		label: 'Copy Application',
		onClick: params.props.onCopyNewBusinessApplication,
		size: ButtonSize.SMALL,
		buttonType: AppThemeColor.SECONDARY,
		endAdornmentIcon: FileCopy,
		disabled: isCopyNewBusinessApplicationButtonDisabled(),
	});

	// Setup the resubmit new business application button hook.
	const resubmitNewBusinessApplicationButton: UseInputButtonResult = useInputButton({
		id: generateIdForResubmitNewBusinessApplicationButton(),
		label: 'Resubmit Application',
		onClick: params.props.onResubmitNewBusinessApplication,
		size: ButtonSize.SMALL,
		buttonType: AppThemeColor.SECONDARY,
		endAdornmentIcon: Replay,
		disabled: isResubmitNewBusinessApplicationButtonDisabled(),
	});

	// Setup the digital signatures button hook.
	const digitalSignaturesButton: UseInputButtonResult = useInputButton({
		id: generateIdForResubmitNewBusinessApplicationButton(),
		label: 'Manage Digital Signatures',
		onClick: params.props.onViewDigitalSignatures,
		size: ButtonSize.SMALL,
		buttonType: AppThemeColor.SECONDARY,
		endAdornmentIcon: Search,
		disabled: isDigitalSignaturesButtonDisabled(),
	});

	const [readyCount, setReadyCount] = React.useState<number>(0);

	// Setup the children.
	const children: JSX.Element[] | JSX.Element | undefined = React.useMemo(() => {
		return params.props.children({
			onChildReady: incrementReadyCount,
		});
	}, [params.props.children]);

	// Get the children count.
	const childrenCount: number = React.useMemo(() => {
		return Array.isArray(children)
			? children.length
			: (children !== undefined
				? 1
				: 0);
	}, [children]);

	const [showStatusMessage, setShowStatusMessage] = React.useState<boolean>(false);

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		if (readyCount === childrenCount && !params.props.isNewBusinessApplicationNotSubmitted) {
			statusDivWrapper.focusToDiv();
		}
	}, [readyCount]);

	// Called when updates are made the props related to the status of the form.
	React.useEffect(() => {
		// Determine if the status message should be rendered.
		if (!params.props.areAllNewBusinessApplicationSectionsComplete
			&& params.props.isNewBusinessApplicationNotSubmitted
			&& params.props.statusMessage !== undefined) {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return true;
			});
		}
		else {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return false;
			});
		}
	}, [params.props.areAllNewBusinessApplicationSectionsComplete, params.props.isNewBusinessApplicationNotSubmitted, params.props.statusMessage]);

	// #endregion life-cycle methods.


	return {
		statusDivWrapper,
		proceedWithPaymentButton,
		underwritingReviewButton,
		newBusinessApplicationPDFButton,
		copyNewBusinessApplicationButton,
		resubmitNewBusinessApplicationButton,
		digitalSignaturesButton,
		children,
		isUnderwritingReviewButtonVisible,
		isResubmitNewBusinessApplicationButtonVisible,
		isDigitalSignaturesButtonVisible,
		applyFormProps,
	};
}

// Export parameters.
export {
	UseFormReviewParameters,
}

// Export results.
export {
	UseFormReviewResults,
}

export default useFormReview;